/**
 * Database module - handles SQLite loading and querying with sql.js
 */

import initSqlJs, { Database } from 'sql.js';
import type { Product, Strain, Dispensary } from './types';

let db: Database | null = null;

/**
 * Initialize and load the SQLite database
 */
export async function initDatabase(): Promise<void> {
  try {
    // Initialize sql.js
    const SQL = await initSqlJs({
      locateFile: (file: string) => `https://sql.js.org/dist/${file}`,
    });

    // Fetch the database file
    const response = await fetch('/data/kind_tree_data.db');
    if (!response.ok) {
      throw new Error(`Failed to fetch database: ${response.statusText}`);
    }

    const buffer = await response.arrayBuffer();
    const sizeKB = (buffer.byteLength / 1024).toFixed(1);
    const lastModified = response.headers.get('last-modified');

    db = new SQL.Database(new Uint8Array(buffer));

    console.log('💾 Database loaded successfully');
    console.log('📦 Database size:', sizeKB, 'KB');
    if (lastModified) {
      console.log('🕐 Database modified:', new Date(lastModified).toLocaleString());
    }
  } catch (error) {
    console.error('Failed to load database:', error);
    throw error;
  }
}

/**
 * Execute a raw SQL query
 */
export function query<T = any>(sql: string, params: any[] = []): T[] {
  if (!db) {
    throw new Error('Database not initialized. Call initDatabase() first.');
  }

  const results: T[] = [];
  const stmt = db.prepare(sql);
  stmt.bind(params);

  while (stmt.step()) {
    const row = stmt.getAsObject();
    results.push(row as T);
  }

  stmt.free();
  return results;
}

/**
 * Get all available products with joined strain, dispensary, and lab data
 */
export function getProducts(): Product[] {
  const sql = `
    SELECT
      p.*,
      s.canonical_name as strain_name,
      s.strain_type,
      d.name as dispensary_name,
      d.location as dispensary_location,
      d.latitude as dispensary_latitude,
      d.longitude as dispensary_longitude,
      d.api_type as dispensary_api_type,
      d.api_base_url as dispensary_api_base_url,
      d.api_config as dispensary_api_config,
      p.data_source as product_data_source,
      l.total_thc,
      l.total_cbd,
      l.cbd,
      l.cbn,
      l.cbg,
      l.cbga,
      l.myrcene,
      l.limonene,
      l.caryophyllene,
      l.pinene,
      l.linalool,
      l.total_terpenes,
      l.terpenes,
      il.id as inferred_lab_id,
      il.total_thc as inferred_total_thc,
      il.total_cbd as inferred_total_cbd,
      il.cbd as inferred_cbd,
      il.cbn as inferred_cbn,
      il.cbg as inferred_cbg,
      il.cbga as inferred_cbga,
      il.myrcene as inferred_myrcene,
      il.limonene as inferred_limonene,
      il.caryophyllene as inferred_caryophyllene,
      il.pinene as inferred_pinene,
      il.linalool as inferred_linalool,
      il.total_terpenes as inferred_total_terpenes,
      il.terpenes as inferred_terpenes
    FROM products p
    JOIN strains s ON p.strain_id = s.id
    JOIN dispensaries d ON p.dispensary_id = d.id
    LEFT JOIN lab_data l ON p.lab_data_id = l.id
    LEFT JOIN lab_data il ON p.inferred_lab_data_id = il.id
    WHERE p.is_available = 1
  `;

  const rows = query(sql);

  return rows.map((row: any) => ({
    id: row.id,
    strain_id: row.strain_id,
    dispensary_id: row.dispensary_id,
    sku: row.sku,
    product_name: row.product_name,
    size_g: row.size_g,
    lab_data_id: row.lab_data_id,
    inferred_lab_data_id: row.inferred_lab_data_id,
    inferred_lab_data_confidence: row.inferred_lab_data_confidence,
    inferred_lab_data_thc_diff: row.inferred_lab_data_thc_diff,
    current_price: row.current_price,
    available_quantity: row.available_quantity,
    is_available: row.is_available === 1,
    is_on_sale: row.is_on_sale === 1,
    last_seen: row.last_seen,
    strain: {
      id: row.strain_id,
      canonical_name: row.strain_name,
      strain_type: row.strain_type,
      is_removed: false,
    },
    data_source: row.product_data_source || null,
    product_url: row.product_url || null,
    dispensary: {
      id: row.dispensary_id,
      name: row.dispensary_name,
      location: row.dispensary_location,
      latitude: row.dispensary_latitude,
      longitude: row.dispensary_longitude,
      api_type: row.dispensary_api_type || '',
      api_base_url: row.dispensary_api_base_url || '',
      api_config: row.dispensary_api_config || '{}',
      is_active: true,
    },
    lab_data: row.lab_data_id
      ? {
          id: row.lab_data_id,
          total_thc: row.total_thc,
          total_cbd: row.total_cbd,
          cbd: row.cbd,
          cbn: row.cbn,
          cbg: row.cbg,
          cbga: row.cbga,
          myrcene: row.myrcene,
          limonene: row.limonene,
          caryophyllene: row.caryophyllene,
          pinene: row.pinene,
          linalool: row.linalool,
          total_terpenes: row.total_terpenes,
          terpenes: row.terpenes,
          terpene_unit: '%',
        }
      : undefined,
    inferred_lab_data: row.inferred_lab_id
      ? {
          id: row.inferred_lab_id,
          total_thc: row.inferred_total_thc,
          total_cbd: row.inferred_total_cbd,
          cbd: row.inferred_cbd,
          cbn: row.inferred_cbn,
          cbg: row.inferred_cbg,
          cbga: row.inferred_cbga,
          myrcene: row.inferred_myrcene,
          limonene: row.inferred_limonene,
          caryophyllene: row.inferred_caryophyllene,
          pinene: row.inferred_pinene,
          linalool: row.inferred_linalool,
          total_terpenes: row.inferred_total_terpenes,
          terpenes: row.inferred_terpenes,
          terpene_unit: '%',
        }
      : undefined,
  }));
}

/**
 * Get all strains
 */
export function getStrains(): Strain[] {
  const sql = 'SELECT * FROM strains ORDER BY canonical_name';
  return query<Strain>(sql);
}

/**
 * Get all dispensaries
 */
export function getDispensaries(): Dispensary[] {
  const sql = 'SELECT * FROM dispensaries WHERE is_active = 1 ORDER BY name';
  return query<Dispensary>(sql);
}

/**
 * Get database statistics
 */
export function getStats() {
  const stats = {
    strains: query('SELECT COUNT(*) as count FROM strains')[0].count,
    dispensaries: query('SELECT COUNT(*) as count FROM dispensaries')[0].count,
    products: query('SELECT COUNT(*) as count FROM products WHERE is_available = 1')[0].count,
    labData: query('SELECT COUNT(*) as count FROM lab_data')[0].count,
  };

  return stats;
}

/**
 * Get the last updated timestamp from the database
 */
export function getLastUpdated(): string | null {
  const result = query<{ last_updated: string }>('SELECT MAX(last_seen) as last_updated FROM products');
  return result[0]?.last_updated || null;
}

/**
 * Get recent activity events (last 24h)
 */
export function getRecentActivity(limit: number = 10) {
  const sql = `
    SELECT DISTINCT
      event_type,
      detected_at,
      sale_type,
      discount_pct,
      product_id,
      strain_name,
      dispensary_name
    FROM (
      SELECT
        'sale' as event_type,
        se.detected_at,
        se.event_type as sale_type,
        se.discount_pct,
        p.id as product_id,
        s.canonical_name as strain_name,
        d.name as dispensary_name
      FROM sale_events se
      JOIN products p ON se.product_id = p.id
      JOIN strains s ON p.strain_id = s.id
      JOIN dispensaries d ON p.dispensary_id = d.id
      WHERE datetime(se.detected_at) >= datetime('now', '-1 day')

      UNION ALL

      SELECT
        'restock' as event_type,
        re.detected_at,
        NULL as sale_type,
        re.qty_change as discount_pct,
        p.id as product_id,
        s.canonical_name as strain_name,
        d.name as dispensary_name
      FROM restock_events re
      JOIN products p ON re.product_id = p.id
      JOIN strains s ON p.strain_id = s.id
      JOIN dispensaries d ON p.dispensary_id = d.id
      WHERE datetime(re.detected_at) >= datetime('now', '-1 day')

      UNION ALL

      SELECT
        'stockout' as event_type,
        so.detected_at,
        NULL as sale_type,
        NULL as discount_pct,
        p.id as product_id,
        s.canonical_name as strain_name,
        d.name as dispensary_name
      FROM stockout_events so
      JOIN products p ON so.product_id = p.id
      JOIN strains s ON p.strain_id = s.id
      JOIN dispensaries d ON p.dispensary_id = d.id
      WHERE datetime(so.detected_at) >= datetime('now', '-1 day')
    )
    ORDER BY detected_at DESC
    LIMIT ?
  `;

  return query(sql, [limit]);
}

/**
 * Get products currently on sale
 */
export function getActiveSales() {
  const sql = `
    SELECT DISTINCT
      p.id,
      p.strain_id,
      p.dispensary_id,
      p.sku,
      p.product_name,
      p.size_g,
      p.lab_data_id,
      p.current_price,
      p.available_quantity,
      p.is_available,
      p.is_on_sale,
      p.last_seen,
      s.canonical_name as strain_name,
      d.name as dispensary_name,
      d.location as dispensary_location,
      MAX(se.discount_pct) as discount_pct,
      MAX(se.detected_at) as sale_start
    FROM products p
    JOIN strains s ON p.strain_id = s.id
    JOIN dispensaries d ON p.dispensary_id = d.id
    LEFT JOIN sale_events se ON p.id = se.product_id AND se.event_type = 'start'
    WHERE p.is_on_sale = 1 AND p.is_available = 1
    GROUP BY p.id
    ORDER BY sale_start DESC
  `;

  return query(sql);
}

/**
 * Get upcoming predictions (next 7 days)
 */
export function getUpcomingPredictions() {
  const sql = `
    SELECT
      pr.prediction_type,
      pr.predicted_date,
      pr.confidence,
      p.id as product_id,
      s.canonical_name as strain_name,
      d.name as dispensary_name
    FROM predictions pr
    JOIN products p ON pr.product_id = p.id
    JOIN strains s ON p.strain_id = s.id
    JOIN dispensaries d ON p.dispensary_id = d.id
    WHERE pr.actual_date IS NULL
      AND date(pr.predicted_date) BETWEEN date('now') AND date('now', '+7 days')
    ORDER BY pr.predicted_date ASC
  `;

  return query(sql);
}

/**
 * Get dispensary statistics
 */
export function getDispensaryStats() {
  const sql = `
    SELECT
      d.name,
      d.location,
      ds.avg_consumption_rate,
      ds.kind_tree_availability_rate,
      ds.active_products
    FROM dispensaries d
    LEFT JOIN dispensary_statistics ds ON d.id = ds.dispensary_id
    WHERE d.is_active = 1
    ORDER BY ds.avg_consumption_rate DESC NULLS LAST
  `;

  return query(sql);
}

/**
 * Get the most recent "came back to market" timestamp for each strain.
 * A strain "came back" when any of its products transitioned from is_available=0 → 1.
 * Returns a map of strain_id → ISO timestamp string.
 */
export function getStrainBackInStock(): Map<number, string> {
  const sql = `
    WITH all_transitions AS (
      SELECT
        p.strain_id,
        ps.is_available,
        ps.timestamp,
        LAG(ps.is_available) OVER (PARTITION BY ps.product_id ORDER BY ps.timestamp) as prev_available
      FROM product_snapshots ps
      JOIN products p ON ps.product_id = p.id
    )
    SELECT strain_id, MAX(timestamp) as back_in_stock
    FROM all_transitions
    WHERE is_available = 1 AND prev_available = 0
    GROUP BY strain_id
  `;
  const rows = query(sql);
  const map = new Map<number, string>();
  rows.forEach((row: any) => map.set(row.strain_id, row.back_in_stock));
  return map;
}

/**
 * Get the first-ever seen timestamp for each strain (MIN snapshot timestamp).
 * Used to show "first appeared" for newly tracked strains with no prior stockout.
 * Returns a map of strain_id → ISO timestamp string.
 */
export function getStrainFirstSeen(): Map<number, string> {
  const sql = `
    SELECT p.strain_id, MIN(ps.timestamp) as first_seen
    FROM product_snapshots ps
    JOIN products p ON ps.product_id = p.id
    WHERE ps.is_available = 1
    GROUP BY p.strain_id
  `;
  const rows = query(sql);
  const map = new Map<number, string>();
  rows.forEach((row: any) => map.set(row.strain_id, row.first_seen));
  return map;
}

/**
 * Close the database connection
 */
export function closeDatabase(): void {
  if (db) {
    db.close();
    db = null;
  }
}
